import type { DropLogRow } from '@/hooks/useDropLogs';

export const DROP_LOG_PAGE_SIZE = 11;

export type DropLogRaceFilter = '' | 'chinese' | 'euro';
export type DropLogTimeFilter = 'all' | 'today' | 'week' | 'month';

export interface DropLogFilterState {
  search: string;
  race: DropLogRaceFilter;
  typePattern: string;
  time: DropLogTimeFilter;
}

export interface WeaponTypeOption {
  pattern: string;
  label: string;
}

export const WEAPON_TYPES: Record<'chinese' | 'euro', Record<string, WeaponTypeOption>> = {
  chinese: {
    spear: { pattern: '_CH_%SPEAR', label: 'Spear' },
    blade: { pattern: '_CH_%BLADE', label: 'Blade' },
    glaive: { pattern: '_CH_%TBLADE', label: 'Glaive' },
    shield: { pattern: '_CH_%SHIELD', label: 'Shield' },
    sword: { pattern: '_CH_%SWORD', label: 'Sword' },
  },
  euro: {
    axe: { pattern: '_EU_%AXE', label: 'Axe' },
    crossbow: { pattern: '_EU_%CROSSBOW', label: 'Crossbow' },
    dagger: { pattern: '_EU_%DAGGER', label: 'Dagger' },
    darkstaff: { pattern: '_EU_%DARKSTAFF', label: 'Dark Staff' },
    harp: { pattern: '_EU_%HARP', label: 'Harp' },
    shield: { pattern: '_EU_%SHIELD', label: 'Shield' },
    staff: { pattern: '_EU_%STAFF', label: 'Staff' },
    sword: { pattern: '_EU_%SWORD', label: 'Sword' },
    tstaff: { pattern: '_EU_%TSTAFF', label: 'Two-Handed Staff' },
    tsword: { pattern: '_EU_%TSWORD', label: 'Two-Handed Sword' },
  },
};

export function getWeaponOptionsForRace(race: DropLogRaceFilter): WeaponTypeOption[] {
  if (race !== 'chinese' && race !== 'euro') return [];
  return Object.values(WEAPON_TYPES[race]);
}

function matchesSearch(row: DropLogRow, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    row.itemName,
    row.charName16,
    row.map,
    row.codeName128,
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

function matchesRaceAndType(
  codeName: string,
  race: DropLogRaceFilter,
  typePattern: string,
): boolean {
  const upper = codeName.toUpperCase();

  if (typePattern) {
    const pattern = typePattern.toUpperCase().replace(/%/g, '');
    return upper.includes(pattern);
  }

  if (race === 'chinese') {
    return upper.includes('_CH_');
  }
  if (race === 'euro') {
    return upper.includes('_EU_');
  }

  return true;
}

function matchesTime(dropDate: string, time: DropLogTimeFilter): boolean {
  if (time === 'all') return true;

  const itemDate = new Date(dropDate);
  if (Number.isNaN(itemDate.getTime())) return true;

  const diffDays = (Date.now() - itemDate.getTime()) / (1000 * 60 * 60 * 24);

  if (time === 'today') return diffDays <= 1;
  if (time === 'week') return diffDays <= 7;
  if (time === 'month') return diffDays <= 30;
  return true;
}

export function filterDropLogs(drops: DropLogRow[], filters: DropLogFilterState): DropLogRow[] {
  return drops.filter((row) => {
    if (!matchesSearch(row, filters.search)) return false;
    if (!matchesRaceAndType(row.codeName128, filters.race, filters.typePattern)) return false;
    if (!matchesTime(row.dropDate, filters.time)) return false;
    return true;
  });
}

export function paginateDropLogs<T>(items: T[], page: number, pageSize = DROP_LOG_PAGE_SIZE): T[] {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function getDropLogPageCount(total: number, pageSize = DROP_LOG_PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

export const DEFAULT_DROP_LOG_FILTERS: DropLogFilterState = {
  search: '',
  race: '',
  typePattern: '',
  time: 'all',
};
